import React, { useState, useMemo, useCallback } from 'react';
import {
    Box, Paper, Typography, alpha, Button, Tabs, Tab, IconButton,
    Autocomplete, TextField, CircularProgress, Chip, Alert, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions,
    Divider, Grid, RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import { ArrowBack, Add, Close, CheckCircle, Download, CloudUpload } from '@mui/icons-material';
import CertificationPreviewSection from './CertificationPreview';
import CertificationInsertForm from './CertificationForm';
import CertificationAmounts from './CertificationAmounts';
import CertificationDraftList from './CertificationDraftList';
import { useClients } from '../../../hooks/comptable/useClients';
import axios from 'axios';

const palette = {
    navy: '#0B1E3C', navy2: '#102B55',
    teal: '#18B6B0', teal2: '#39D0C9', tealLight: '#E0F7F6',
    green: '#7BC043', gold: '#F3C15C', paper: '#FFFFFF',
    gradient: 'linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)',
    shadow: '0 10px 40px rgba(11, 30, 60, 0.08)',
    textPrimary: '#1E293B', textSecondary: '#64748B',
};

const initialFormData = {
    paymentDate: '', declarantNumber: '', billingExercise: '',
    operationType: '', operation: '', operationCode: '', operationLabel: '',
    tauxRS: '', isSupported: '', hasConvention: '', rsaTva: '', tauxRsaTva: '',
};
const initialFormErrors = {
    paymentDate: '', declarantNumber: '', billingExercise: '',
    operationType: '', operation: '', isSupported: '', hasConvention: '',
};
const initialAmountsData = {
    montantHT: '', tauxTVA: '19', montantTVA: '0.000', montantTTC: '0.000',
    montantRS: '0.000', montantRsaTva: '0.000', netAPayer: '0.000',
};

const createEmptyCertState = () => ({
    step: 'preview',
    verificationData: { identifierType: 1, identifier: '' },
    beneficiary: null,
    formData: { ...initialFormData },
    formErrors: { ...initialFormErrors },
    amountsData: { ...initialAmountsData },
    amountsErrors: { montantHT: '' },
});

const CertificationPage = () => {
    const [view, setView] = useState('list');
    const [selectedClientId, setSelectedClientId] = useState('');
    const [editingDraftId, setEditingDraftId] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [certStates, setCertStates] = useState([createEmptyCertState()]);
    const [sendMode, setSendMode] = useState('later');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [result, setResult] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const { clients, loading: clientsLoading } = useClients();

    const selectedClient = useMemo(() => {
        if (!selectedClientId) return null;
        return clients.find(c => c._id === selectedClientId) || null;
    }, [selectedClientId, clients]);

    const resetWizard = () => {
        setSelectedClientId('');
        setEditingDraftId(null);
        setActiveTab(0);
        setCertStates([createEmptyCertState()]);
        setSendMode('later');
        setSubmitting(false);
        setError('');
        setSuccess(false);
        setResult(null);
        setConfirmOpen(false);
    };

    const handleNewCertificate = () => { resetWizard(); setView('wizard'); };

    const handleEditDraft = (draft) => {
        setEditingDraftId(draft._id);
        setSelectedClientId(typeof draft.clientId === 'object' ? draft.clientId._id : draft.clientId);

        const states = (draft.certificates || []).map(cert => {
            // Restaurer le step sauvegarde, sinon deduire
            let step = cert.step || 'preview';
            if (!cert.step) {
                const hasAmounts = cert.amounts?.montantHT != null && cert.amounts.montantHT > 0;
                const hasOperation = cert.operation?.code;
                const hasCertInfo = cert.certificate?.paymentDate;
                const hasBeneficiary = cert.beneficiary?.identifier;
                if (hasAmounts && hasOperation && hasCertInfo) step = 'done';
                else if (hasOperation && hasCertInfo) step = 'amounts';
                else if (hasBeneficiary) step = 'form';
            }

            return {
                step,
                verificationData: {
                    identifierType: cert.beneficiary?.identifierType || 1,
                    identifier: cert.beneficiary?.identifier || '',
                },
                beneficiary: cert.beneficiary ? {
                    nometprenonOuRaisonsociale: cert.beneficiary.name || '',
                    activity: cert.beneficiary.activity || '',
                    address: cert.beneficiary.address || '',
                    email: cert.beneficiary.email || '',
                    phoneNumber: cert.beneficiary.phoneNumber || '',
                    resident: cert.beneficiary.resident ?? true,
                    identifier: cert.beneficiary.identifier || '',
                    taxpayerType: cert.beneficiary.categorieContribuable || 'PP',
                } : null,
                formData: {
                    paymentDate: cert.certificate?.paymentDate || '',
                    declarantNumber: cert.certificate?.declarantNumber || '',
                    billingExercise: cert.certificate?.billingExercise || '',
                    operationType: cert.certificate?.operationType || '',
                    operation: cert.operation?.code || '',
                    operationCode: cert.operation?.code || '',
                    operationLabel: cert.operation?.label || '',
                    tauxRS: cert.operation?.tauxRS ?? cert.amounts?.tauxRS ?? '',
                    isSupported: cert.certificate?.isSupported ? 'oui' : 'non',
                    hasConvention: cert.certificate?.hasConvention ? 'oui' : 'non',
                    rsaTva: cert.amounts?.rsaTva || '',
                    tauxRsaTva: cert.amounts?.tauxRsaTva != null ? String(cert.amounts.tauxRsaTva) : '',
                },
                formErrors: { ...initialFormErrors },
                amountsData: {
                    montantHT: cert.amounts?.montantHT != null ? String(cert.amounts.montantHT) : '',
                    tauxTVA: cert.amounts?.tauxTVA != null ? String(cert.amounts.tauxTVA) : '19',
                    montantTVA: cert.amounts?.montantTVA != null ? Number(cert.amounts.montantTVA).toFixed(3) : '0.000',
                    montantTTC: cert.amounts?.montantTTC != null ? Number(cert.amounts.montantTTC).toFixed(3) : '0.000',
                    montantRS: cert.amounts?.montantRS != null ? Number(cert.amounts.montantRS).toFixed(3) : '0.000',
                    montantRsaTva: cert.amounts?.montantRsaTva != null ? Number(cert.amounts.montantRsaTva).toFixed(3) : '0.000',
                    netAPayer: cert.amounts?.netAPayer != null ? Number(cert.amounts.netAPayer).toFixed(3) : '0.000',
                },
                amountsErrors: { montantHT: '' },
            };
        });

        if (states.length === 0) states.push(createEmptyCertState());
        setCertStates(states);
        setActiveTab(0);
        setView('wizard');
    };

    // Helpers pour l'onglet actif
    const updateField = useCallback((field, valOrFn) => {
        setCertStates(prev => {
            const next = [...prev];
            const cur = next[activeTab][field];
            next[activeTab] = { ...next[activeTab], [field]: typeof valOrFn === 'function' ? valOrFn(cur) : valOrFn };
            return next;
        });
    }, [activeTab]);

    const setStep = useCallback((s) => updateField('step', s), [updateField]);
    const setVerificationData = useCallback((v) => updateField('verificationData', v), [updateField]);
    const setBeneficiary = useCallback((v) => updateField('beneficiary', v), [updateField]);
    const setFormData = useCallback((v) => updateField('formData', v), [updateField]);
    const setFormErrors = useCallback((v) => updateField('formErrors', v), [updateField]);
    const setAmountsData = useCallback((v) => updateField('amountsData', v), [updateField]);
    const setAmountsErrors = useCallback((v) => updateField('amountsErrors', v), [updateField]);

    const addCertTab = () => {
        setCertStates(prev => [...prev, createEmptyCertState()]);
        setActiveTab(certStates.length);
    };

    const removeCertTab = (index) => {
        if (certStates.length <= 1) return;
        setCertStates(prev => prev.filter((_, i) => i !== index));
        if (activeTab >= index && activeTab > 0) setActiveTab(prev => prev - 1);
        else if (activeTab >= certStates.length - 1) setActiveTab(certStates.length - 2);
    };

    const completedCount = certStates.filter(s => s.step === 'done').length;
    const allDone = completedCount === certStates.length && certStates.length > 0;

    const buildGroupPayload = () => certStates.map(cs => ({
        step: cs.step,
        beneficiary: {
            identifierType: cs.verificationData.identifierType,
            identifier: cs.verificationData.identifier,
            name: cs.beneficiary?.nometprenonOuRaisonsociale || '',
            activity: cs.beneficiary?.activity || '',
            address: cs.beneficiary?.address || '',
            email: cs.beneficiary?.email || '',
            phoneNumber: cs.beneficiary?.phoneNumber || '',
            resident: cs.beneficiary?.resident ?? true,
            categorieContribuable: cs.beneficiary?.taxpayerType || 'PP',
        },
        certificate: {
            paymentDate: cs.formData.paymentDate,
            declarantNumber: cs.formData.declarantNumber,
            billingExercise: cs.formData.billingExercise,
            operationType: cs.formData.operationType,
            isSupported: cs.formData.isSupported === 'oui',
            hasConvention: cs.formData.hasConvention === 'oui',
        },
        operation: {
            code: cs.formData.operationCode,
            label: cs.formData.operationLabel,
            tauxRS: parseFloat(cs.formData.tauxRS) || 0,
        },
        amounts: {
            montantHT: parseFloat(cs.amountsData.montantHT) || 0,
            tauxTVA: parseFloat(cs.amountsData.tauxTVA) || 0,
            montantTVA: parseFloat(cs.amountsData.montantTVA) || 0,
            montantTTC: parseFloat(cs.amountsData.montantTTC) || 0,
            tauxRS: parseFloat(cs.formData.tauxRS) || 0,
            montantRS: parseFloat(cs.amountsData.montantRS) || 0,
            rsaTva: cs.formData.rsaTva || 'RSATVA0',
            tauxRsaTva: cs.formData.rsaTva === 'RSATVA25' ? (parseFloat(cs.formData.tauxRsaTva) || 25) : 0,
            montantRsaTva: parseFloat(cs.amountsData.montantRsaTva) || 0,
            netAPayer: parseFloat(cs.amountsData.netAPayer) || 0,
        },
    }));

    const handleSubmit = async () => {
        setError('');
        setSubmitting(true);
        try {
            const certificates = buildGroupPayload();
            if (sendMode === 'now') {
                const response = await axios.post(`/api/certif/submit/${selectedClientId}`, { certificates });
                setResult(response.data);
            } else {
                if (editingDraftId) {
                    await axios.put(`/api/certif/drafts/${editingDraftId}`, { certificates });
                } else {
                    await axios.post('/api/certif/drafts', { clientId: selectedClientId, certificates });
                }
                setResult({ saved: true });
            }
            setSuccess(true);
        } catch (err) {
            setError(err?.response?.data?.message || 'Erreur lors du traitement.');
        } finally {
            setSubmitting(false);
        }
    };

    const [downloading, setDownloading] = useState(false);

    const handleDownloadXml = async () => {
        setError('');
        setDownloading(true);
        try {
            const certificates = buildGroupPayload();
            // Sauvegarder d'abord pour avoir un draftId
            let draftId = editingDraftId;
            if (!draftId) {
                const res = await axios.post('/api/certif/drafts', { clientId: selectedClientId, certificates });
                draftId = res.data?.data?._id;
                setEditingDraftId(draftId);
            } else {
                await axios.put(`/api/certif/drafts/${draftId}`, { certificates });
            }
            // Telecharger le XML
            const response = await axios.post('/api/certif/drafts/export-xml', { draftIds: [draftId] }, { responseType: 'blob' });
            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?(.+?)"?$/);
            const filename = match ? match[1] : 'certificats.xml';
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            const text = err?.response?.data instanceof Blob
                ? JSON.parse(await err.response.data.text()).message
                : err?.response?.data?.message || "Erreur lors du telechargement XML.";
            setError(text);
        } finally {
            setDownloading(false);
        }
    };

    const [uploading, setUploading] = useState(false);

    const handleUploadXml = async () => {
        setError('');
        setUploading(true);
        try {
            const certificates = buildGroupPayload();
            // Sauvegarder d'abord pour avoir un draftId
            let draftId = editingDraftId;
            if (!draftId) {
                const res = await axios.post('/api/certif/drafts', { clientId: selectedClientId, certificates });
                draftId = res.data?.data?._id;
                setEditingDraftId(draftId);
            } else {
                await axios.put(`/api/certif/drafts/${draftId}`, { certificates });
            }
            // Deposer le XML sur TEJ
            const response = await axios.post('/api/certif/drafts/upload-xml', { draftIds: [draftId] });
            setResult({ uploaded: true, data: response.data });
            setSuccess(true);
        } catch (err) {
            setError(err?.response?.data?.message || 'Erreur lors du depot XML sur TEJ.');
        } finally {
            setUploading(false);
        }
    };

    const handleBack = () => { resetWizard(); setView('list'); setRefreshKey(k => k + 1); };
    const handleSuccess = () => { resetWizard(); setView('list'); setRefreshKey(k => k + 1); };

    const formatAmt = (v) => { const n = parseFloat(v); return isNaN(n) ? '0.000' : n.toFixed(3) + ' TND'; };

    const activeState = certStates[activeTab] || createEmptyCertState();

    // ═══════════════════════════════════════════════
    //  VUE LISTE
    // ═══════════════════════════════════════════════
    if (view === 'list') {
        return (
            <Box sx={{ minHeight: '100vh', background: palette.gradient, py: 4, px: 2 }}>
                <Paper elevation={0} sx={{ p: 0, borderRadius: 4, overflow: 'hidden', bgcolor: 'white', boxShadow: palette.shadow, border: `1px solid ${alpha(palette.teal, 0.1)}` }}>
                    <Box sx={{ py: 3, px: 4, background: `linear-gradient(135deg, ${palette.navy} 0%, rgba(20,45,80,0.85) 100%)`, color: 'white' }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Certificats de retenue a la source</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                            Gerez vos certificats : creez, modifiez et envoyez-les a TEJ.
                        </Typography>
                    </Box>
                    <Box sx={{ p: 4 }}>
                        <CertificationDraftList
                            key={refreshKey}
                            onNewCertificate={handleNewCertificate}
                            onEditDraft={handleEditDraft}
                            palette={palette}
                        />
                    </Box>
                </Paper>
            </Box>
        );
    }

    // ═══════════════════════════════════════════════
    //  VUE WIZARD
    // ═══════════════════════════════════════════════
    return (
        <Box sx={{ minHeight: '100vh', background: palette.gradient, py: 4, px: 2 }}>
            <Paper elevation={0} sx={{ p: 0, borderRadius: 4, overflow: 'hidden', bgcolor: 'white', boxShadow: palette.shadow, border: `1px solid ${alpha(palette.teal, 0.1)}` }}>

                {/* Header */}
                <Box sx={{ py: 3, px: 4, background: `linear-gradient(135deg, ${palette.navy} 0%, rgba(20,45,80,0.85) 100%)`, color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button onClick={handleBack} sx={{ minWidth: 'auto', color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }} variant="outlined" size="small" startIcon={<ArrowBack />}>
                        Retour
                    </Button>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {editingDraftId ? 'Modifier les certificats' : 'Nouveaux certificats'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                            Selectionnez un client puis ajoutez un ou plusieurs certificats.
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ p: 4 }}>
                    {/* Succes */}
                    {success ? (
                        <Box>
                            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                                {result?.saved
                                    ? `${certStates.length} certificat(s) sauvegarde(s) en brouillon.`
                                    : result?.uploaded
                                        ? `${certStates.length} certificat(s) deposes sur TEJ via XML avec succes !`
                                        : `${certStates.length} certificat(s) soumis a TEJ avec succes !`
                                }
                            </Alert>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Button variant="contained" onClick={handleSuccess} sx={{ px: 4, py: 1.2, borderRadius: 2, fontWeight: 700, textTransform: 'none', background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.teal2} 100%)` }}>
                                    Retour a la liste
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            {/* Selection client */}
                            <Box sx={{ mb: 3 }}>
                                <Autocomplete
                                    fullWidth
                                    options={clients}
                                    value={selectedClient}
                                    onChange={(_, newValue) => setSelectedClientId(newValue?._id || '')}
                                    disabled={!!editingDraftId}
                                    getOptionLabel={(option) => {
                                        const name = option.raisonSociale || `${option.prenom || ''} ${option.nom || ''}`.trim();
                                        const code = option.codeClient || '';
                                        return code ? `${name} (${code})` : name;
                                    }}
                                    filterOptions={(options, { inputValue }) => {
                                        const search = inputValue.toLowerCase().trim();
                                        if (!search) return options;
                                        return options.filter((c) => {
                                            const name = (c.raisonSociale || `${c.prenom || ''} ${c.nom || ''}`).toLowerCase();
                                            const code = (c.codeClient || '').toLowerCase();
                                            return name.includes(search) || code.includes(search);
                                        });
                                    }}
                                    loading={clientsLoading}
                                    noOptionsText="Aucun client trouve"
                                    renderInput={(params) => (
                                        <TextField {...params} label="Client declarant" placeholder="Rechercher par nom ou code client..."
                                            InputProps={{ ...params.InputProps, endAdornment: (<>{clientsLoading ? <CircularProgress size={20} /> : null}{params.InputProps.endAdornment}</>) }}
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option._id === value?._id}
                                />
                            </Box>

                            {/* Tabs + contenu */}
                            {selectedClientId && (
                                <>
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, display: 'flex', alignItems: 'center' }}>
                                        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ flex: 1 }}>
                                            {certStates.map((cs, i) => (
                                                <Tab
                                                    key={i}
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {cs.step === 'done' && <CheckCircle sx={{ fontSize: 16, color: palette.green }} />}
                                                            <span>{cs.beneficiary?.nometprenonOuRaisonsociale || `Certificat ${i + 1}`}</span>
                                                            {certStates.length > 1 && (
                                                                <Close
                                                                    sx={{ fontSize: 16, ml: 0.5, color: palette.textSecondary, '&:hover': { color: '#d32f2f' } }}
                                                                    onClick={(e) => { e.stopPropagation(); removeCertTab(i); }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            ))}
                                        </Tabs>
                                        <Button
                                            onClick={addCertTab}
                                            startIcon={<Add />}
                                            size="small"
                                            sx={{ ml: 1, textTransform: 'none', fontWeight: 600, color: palette.teal, whiteSpace: 'nowrap' }}
                                        >
                                            Ajouter certificat
                                        </Button>
                                    </Box>

                                    {/* Contenu de l'onglet actif */}
                                    {activeState.step === 'done' ? (
                                        <CertDoneSummary
                                            certState={activeState}
                                            onEdit={() => setStep('preview')}
                                            palette={palette}
                                            formatAmt={formatAmt}
                                        />
                                    ) : (
                                        <>
                                            {activeState.step === 'preview' && (
                                                <CertificationPreviewSection
                                                    fixedClientId={selectedClientId}
                                                    verificationData={{ ...activeState.verificationData, selectedClientId }}
                                                    setVerificationData={setVerificationData}
                                                    beneficiary={activeState.beneficiary}
                                                    setBeneficiary={setBeneficiary}
                                                    onContinue={() => setStep('form')}
                                                    isVisible={true}
                                                    palette={palette}
                                                />
                                            )}
                                            {activeState.step === 'form' && (
                                                <CertificationInsertForm
                                                    clientId={selectedClientId}
                                                    beneficiary={activeState.beneficiary}
                                                    formData={activeState.formData}
                                                    setFormData={setFormData}
                                                    formErrors={activeState.formErrors}
                                                    setFormErrors={setFormErrors}
                                                    onBack={() => setStep('preview')}
                                                    onNext={() => setStep('amounts')}
                                                    palette={palette}
                                                />
                                            )}
                                            {activeState.step === 'amounts' && (
                                                <CertificationAmounts
                                                    clientId={selectedClientId}
                                                    beneficiary={activeState.beneficiary}
                                                    formData={activeState.formData}
                                                    amountsData={activeState.amountsData}
                                                    setAmountsData={setAmountsData}
                                                    amountsErrors={activeState.amountsErrors}
                                                    setAmountsErrors={setAmountsErrors}
                                                    onBack={() => setStep('form')}
                                                    onNext={() => setStep('done')}
                                                    palette={palette}
                                                />
                                            )}
                                        </>
                                    )}

                                    {/* Barre d'actions en bas */}
                                    <Divider sx={{ my: 3 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                        <Chip
                                            label={`${completedCount}/${certStates.length} certificat(s) complete(s)`}
                                            color={allDone ? 'success' : 'default'}
                                            variant={allDone ? 'filled' : 'outlined'}
                                        />

                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                            {/* Sauvegarder toujours disponible */}
                                            <Button
                                                variant="outlined"
                                                onClick={() => { setSendMode('later'); setConfirmOpen(true); }}
                                                disabled={submitting}
                                                sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                                            >
                                                {submitting && sendMode === 'later' ? <CircularProgress size={22} /> : 'Sauvegarder'}
                                            </Button>

                                            {/* Telecharger XML uniquement si tous sont done */}
                                            {allDone && (
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleDownloadXml}
                                                    disabled={downloading || submitting}
                                                    startIcon={downloading ? <CircularProgress size={18} /> : <Download />}
                                                    sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                                                >
                                                    Telecharger XML
                                                </Button>
                                            )}

                                            {/* Deposer XML sur TEJ uniquement si tous sont done */}
                                            {allDone && (
                                                <Button
                                                    variant="contained"
                                                    onClick={handleUploadXml}
                                                    disabled={uploading || submitting || downloading}
                                                    startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
                                                    sx={{
                                                        px: 3, py: 1.2, borderRadius: 2, fontWeight: 700, textTransform: 'none',
                                                        background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.teal2} 100%)`,
                                                        '&:hover': { background: `linear-gradient(135deg, ${palette.teal2} 0%, ${palette.green} 100%)` },
                                                    }}
                                                >
                                                    Deposer XML sur TEJ
                                                </Button>
                                            )}

                                            {/* Envoyer uniquement si tous sont done */}
                                            {allDone && (
                                                <Button
                                                    variant="contained"
                                                    onClick={() => { setSendMode('now'); setConfirmOpen(true); }}
                                                    disabled={submitting}
                                                    sx={{
                                                        px: 4, py: 1.2, borderRadius: 2, fontWeight: 700, textTransform: 'none',
                                                        background: `linear-gradient(135deg, ${palette.navy} 0%, ${palette.navy2} 100%)`,
                                                        '&:hover': { background: `linear-gradient(135deg, ${palette.navy2} 0%, ${palette.teal} 100%)` },
                                                    }}
                                                >
                                                    {submitting && sendMode === 'now' ? <CircularProgress size={22} color="inherit" /> : 'Envoyer a TEJ'}
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>

                                    {!allDone && (
                                        <Typography variant="caption" sx={{ mt: 1, color: palette.textSecondary, display: 'block' }}>
                                            Completez tous les certificats pour pouvoir les envoyer a TEJ. Vous pouvez sauvegarder a tout moment.
                                        </Typography>
                                    )}

                                    {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Paper>

            {/* Dialog confirmation */}
            <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)}>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {sendMode === 'now' ? "Confirmer l'envoi a TEJ" : 'Confirmer la sauvegarde'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {sendMode === 'now'
                            ? `${certStates.length} certificat(s) seront soumis sur la plateforme TEJ. Cette action est irreversible.`
                            : `${certStates.length} certificat(s) seront sauvegardes en brouillon.`
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Annuler</Button>
                    <Button onClick={() => { setConfirmOpen(false); handleSubmit(); }} variant="contained" disabled={submitting} color={sendMode === 'now' ? 'primary' : 'success'}>
                        {submitting ? <CircularProgress size={20} /> : 'Confirmer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// ═══════════════════════════════════════════════
//  Mini-resume quand un onglet est "done"
// ═══════════════════════════════════════════════

const CertDoneSummary = ({ certState, onEdit, palette, formatAmt }) => {
    const cs = certState;
    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `2px solid ${alpha(palette.green, 0.3)}`, backgroundColor: alpha(palette.green, 0.03) }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: palette.green }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: palette.textPrimary }}>Certificat complete</Typography>
                </Box>
                <Button variant="outlined" size="small" onClick={onEdit} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    Modifier
                </Button>
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ color: palette.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>Beneficiaire</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{cs.beneficiary?.nometprenonOuRaisonsociale || '-'}</Typography>
                    <Typography variant="body2" sx={{ color: palette.textSecondary }}>{cs.verificationData.identifier}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ color: palette.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>Operation</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{cs.formData.operationLabel || cs.formData.operationCode || '-'}</Typography>
                    <Typography variant="body2" sx={{ color: palette.textSecondary }}>Date: {cs.formData.paymentDate || '-'}</Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: palette.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>Montant HT</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatAmt(cs.amountsData.montantHT)}</Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: palette.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>Montant RS</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatAmt(cs.amountsData.montantRS)}</Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: palette.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>Net a payer</Typography>
                    <Typography sx={{ fontWeight: 700, color: palette.teal }}>{formatAmt(cs.amountsData.netAPayer)}</Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default CertificationPage;
